import { AlreadySignedInScreen } from "@/features/auth/screens/AlreadySignedInScreen";
import { AuthCheckingScreen } from "@/features/auth/screens/AuthCheckingScreen";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useLogoutMutation } from "@/features/auth/api/useLogoutMutation";
import { usersOrganisationsRolesQueryOptions, useSetRoles } from "@/features/auth/api/selectUsersOrganisationsRoles";
import { getAuthenticatedPublicLanding } from "@/features/auth/utils/getAuthenticatedPublicLanding";
import { selectProfileByIdQueryOptions } from "@/features/profile/api/selectProfile";
import { useAuthCopy } from "@/features/i18n/authCopy";
import { useAuth } from "@/utils/auth";
import { useQuery } from "@tanstack/react-query";
import { authQueryOptions } from "../../features/auth/api/useAuthQuery";
import useAuthQuery from "../../features/auth/api/useAuthQuery";

export const Route = createFileRoute("/_auth/_public")({
    component: PublicAuth,

    loader: async ({ context }) => {
        const user = await context?.queryClient?.ensureQueryData(authQueryOptions());

        if (user?.id) {
            const roles = await context?.queryClient?.ensureQueryData(usersOrganisationsRolesQueryOptions(user.id));
            context?.auth?.roles?.setRoles(roles ?? []);
        }

        return context;
    },
});

function PublicAuth() {
    const copy = useAuthCopy();
    const logout = useLogoutMutation();
    const { data: authUser, isLoading: isAuthLoading } = useAuthQuery();
    const auth = useAuth();
    useSetRoles();

    const userId = authUser?.id ?? "";
    const { data: profile, isLoading: isProfileLoading } = useQuery({
        ...selectProfileByIdQueryOptions(userId),
        enabled: Boolean(userId),
    });

    if (isAuthLoading || (authUser && isProfileLoading)) {
        return <AuthCheckingScreen />;
    }

    if (authUser && profile) {
        const landing = getAuthenticatedPublicLanding(profile, auth.roles, copy);

        return (
            <AlreadySignedInScreen
                title={landing.title}
                description={landing.description}
                continueLabel={landing.buttonText}
                continueTo={landing.to}
                continueSearch={landing.search}
                onLogout={() => logout.mutate()}
            />
        );
    }

    return <Outlet />;
}
